import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError, AUTH_EXPIRED_EVENT } from "@/lib/api";
import { AuthContext } from "@/lib/auth";
import { resumePreviewSignIn } from "@/lib/preview-auth";
import { queryClient } from "@/lib/query/client";
import { ROLE_LABELS, type InvitableRole, type User } from "@/lib/types";

/** `?invite=<token>` — the link an admin copies out of the Members dialog. */
const inviteTokenFromUrl = (): string | null => new URLSearchParams(window.location.search).get("invite");

const clearInviteFromUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete("invite");
  window.history.replaceState(null, "", url.toString());
};

type Screen =
  | { kind: "loading" }
  | { kind: "offline"; message: string }
  | { kind: "setup" }
  | { kind: "login" }
  | { kind: "invite"; token: string }
  | { kind: "in"; user: User };

/**
 * Nothing in the app renders until this knows who is looking at it. It asks the
 * server once, then shows first-run setup, an invite, the sign-in form, or the app
 * — and drops back to sign-in whenever a request comes back 401.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const [screen, setScreen] = useState<Screen>({ kind: "loading" });
  const offline = useRef(false);
  offline.current = screen.kind === "offline";

  const resolve = useCallback(async () => {
    try {
      const state = await api.authState();
      const invite = inviteTokenFromUrl();
      if (state.needsSetup) setScreen({ kind: "setup" });
      else if (invite && !state.user) setScreen({ kind: "invite", token: invite });
      else if (state.user) {
        // Signed in on the way to a preview: back to its sign-in step, not the app.
        if (resumePreviewSignIn()) return;
        if (invite) clearInviteFromUrl();
        setScreen({ kind: "in", user: state.user });
      } else setScreen({ kind: "login" });
    } catch (error) {
      const message = !navigator.onLine
        ? "You’re offline. The board will load as soon as this device is back online."
        : error instanceof ApiError
          ? error.message
          : "The board API is not reachable.";
      setScreen({ kind: "offline", message });
    }
  }, []);

  useEffect(() => {
    void resolve();
    const onExpired = () => setScreen((current) => (current.kind === "in" ? { kind: "login" } : current));
    // The installed app can open from its cached shell with no network; retry on reconnect.
    const onOnline = () => {
      if (offline.current) void resolve();
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
      window.removeEventListener("online", onOnline);
    };
  }, [resolve]);

  // The cache holds what one person may see; signing out or in as someone else
  // must not show the next person the last one's boards, even for a frame.
  const signedInAs = screen.kind === "in" ? screen.user.id : null;
  const cachedFor = useRef<string | null>(null);
  useEffect(() => {
    if (signedInAs === cachedFor.current) return;
    if (cachedFor.current !== null) queryClient.clear();
    cachedFor.current = signedInAs;
  }, [signedInAs]);

  const logout = useCallback(async () => {
    await api.logout().catch(() => undefined);
    setScreen({ kind: "login" });
  }, []);

  const signedIn = (user: User) => {
    if (resumePreviewSignIn()) {
      setScreen({ kind: "loading" });
      return;
    }
    clearInviteFromUrl();
    setScreen({ kind: "in", user });
  };

  if (screen.kind === "in") {
    return <AuthContext.Provider value={{ user: screen.user, logout }}>{children}</AuthContext.Provider>;
  }

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm rounded-xl border border-border/70 bg-surface p-6 shadow-sm">
        <div className="mb-5 flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="size-6 rounded-md" />
          <span className="text-[15px] font-semibold">Board</span>
        </div>
        {screen.kind === "loading" ? (
          <p className="flex items-center gap-2 text-[13px] text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Checking your session…
          </p>
        ) : screen.kind === "offline" ? (
          <div className="space-y-3">
            <p className="text-[13px] text-muted-foreground">{screen.message}</p>
            <Button size="sm" variant="outline" onClick={() => void resolve()}>
              Try again
            </Button>
          </div>
        ) : screen.kind === "setup" ? (
          <SetupForm onDone={signedIn} />
        ) : screen.kind === "invite" ? (
          <InviteForm token={screen.token} onDone={signedIn} onSignIn={() => setScreen({ kind: "login" })} />
        ) : (
          <LoginForm onDone={signedIn} />
        )}
      </div>
    </div>
  );
}

function useSubmit<T>(action: () => Promise<T>, onDone: (value: T) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onDone(await action());
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Something went wrong — try again.");
    } finally {
      setBusy(false);
    }
  };
  return { busy, error, submit };
}

const Field = ({ id, label, children }: { id: string; label: string; children: ReactNode }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    {children}
  </div>
);

const ErrorLine = ({ error }: { error: string | null }) =>
  error ? <p className="text-[12.5px] text-destructive">{error}</p> : null;

function LoginForm({ onDone }: { onDone: (user: User) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { busy, error, submit } = useSubmit(() => api.login({ email, password }), ({ user }) => onDone(user));
  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <h1 className="text-[16px] font-semibold">Sign in</h1>
        <p className="text-[12.5px] text-muted-foreground">Use the email you set up or were invited with.</p>
      </div>
      <Field id="login-email" label="Email">
        <Input id="login-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <Field id="login-password" label="Password">
        <Input
          id="login-password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      <ErrorLine error={error} />
      <Button type="submit" className="w-full" disabled={busy}>
        {busy ? <Loader2 className="animate-spin" /> : null} Sign in
      </Button>
    </form>
  );
}

function SetupForm({ onDone }: { onDone: (user: User) => void }) {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { busy, error, submit } = useSubmit(
    () => api.setup({ email, password, displayName: displayName.trim() || undefined }),
    ({ user }) => onDone(user),
  );
  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <h1 className="text-[16px] font-semibold">Set up this board</h1>
        <p className="text-[12.5px] text-muted-foreground">
          You become its owner. Everyone else joins by an invite you send from Members.
        </p>
      </div>
      <Field id="setup-name" label="Your name">
        <Input id="setup-name" autoComplete="name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Me" />
      </Field>
      <Field id="setup-email" label="Email">
        <Input id="setup-email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <Field id="setup-password" label="Password (8+ characters)">
        <Input
          id="setup-password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      <ErrorLine error={error} />
      <Button type="submit" className="w-full" disabled={busy}>
        {busy ? <Loader2 className="animate-spin" /> : null} Create owner account
      </Button>
    </form>
  );
}

function InviteForm({ token, onDone, onSignIn }: { token: string; onDone: (user: User) => void; onSignIn: () => void }) {
  const [invite, setInvite] = useState<{ email: string; role: InvitableRole; invitedByName: string } | null>(null);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const { busy, error, submit } = useSubmit(
    () => api.acceptInvite(token, { displayName, password }),
    ({ user }) => onDone(user),
  );

  useEffect(() => {
    api
      .previewInvite(token)
      .then(setInvite)
      .catch((caught) => setInvalid(caught instanceof ApiError ? caught.message : "This invite could not be read."));
  }, [token]);

  if (invalid) {
    return (
      <div className="space-y-3">
        <h1 className="text-[16px] font-semibold">This invite is not valid</h1>
        <p className="text-[12.5px] text-muted-foreground">It may have expired or already been used. Ask for a new one.</p>
        <Button size="sm" variant="outline" onClick={onSignIn}>
          I already have an account
        </Button>
      </div>
    );
  }
  if (!invite) {
    return (
      <p className="flex items-center gap-2 text-[13px] text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Opening your invite…
      </p>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <h1 className="text-[16px] font-semibold">Join the board</h1>
        <p className="text-[12.5px] text-muted-foreground">
          {invite.invitedByName} invited <span className="font-medium text-foreground">{invite.email}</span> as{" "}
          {ROLE_LABELS[invite.role].toLowerCase()}.
        </p>
      </div>
      <Field id="invite-name" label="Your name">
        <Input id="invite-name" autoComplete="name" required value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </Field>
      <Field id="invite-password" label="Choose a password (8+ characters)">
        <Input
          id="invite-password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </Field>
      <ErrorLine error={error} />
      <Button type="submit" className="w-full" disabled={busy}>
        {busy ? <Loader2 className="animate-spin" /> : null} Join
      </Button>
    </form>
  );
}
